import '@/assets/styles/components/create-password.styles.scss';
import React, { useState, useEffect, useRef } from 'react';
import { CreatePasswordProps } from './create-password.types';
import { FormInput } from '@/components';

export const CreatePassword: React.FC<CreatePasswordProps> = ({ onNext, onProgress, disabled }) => {
	const [password, setPassword] = useState('');
	const [confirmPassword, setConfirmPassword] = useState('');
	const [isFormValid, setIsFormValid] = useState(false);
	const [showMismatchError, setShowMismatchError] = useState(false);

	const formRef = useRef<HTMLFormElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);

	useEffect(() => {
		const fixPagePosition = () => {
			const bodyStyle = document.body.style;
			const height = window.innerHeight;
			const width = window.innerWidth;

			bodyStyle.position = 'fixed';
			bodyStyle.width = width + 'px';
			bodyStyle.height = height + 'px';
			bodyStyle.overflow = 'hidden';

			return () => {
				bodyStyle.position = '';
				bodyStyle.width = '';
				bodyStyle.height = '';
				bodyStyle.overflow = '';
			};
		};

		const styleInputs = () => {
			const style = document.createElement('style');
			style.innerHTML = `
        .password-form-section {
          position: relative;
          z-index: 100;
          transform: translateZ(0);
          will-change: transform;
        }
        .password-form-section input {
          transform: translateZ(0);
          will-change: transform;
        }
      `;
			document.head.appendChild(style);

			return () => {
				document.head.removeChild(style);
			};
		};

		const cleanup2 = fixPagePosition();
		const cleanup3 = styleInputs();

		return () => {
			cleanup2();
			cleanup3();
		};
	}, []);

	const validatePasswords = (newPassword: string, newConfirmPassword: string) => {
		if (newPassword.length === 6 && newConfirmPassword.length === 6) {
			const isValid = newPassword === newConfirmPassword;
			setIsFormValid(isValid);
			setShowMismatchError(!isValid);

			onProgress({
				password: isValid ? newPassword : '',
				confirmPassword: isValid ? newConfirmPassword : ''
			});
		} else {
			setShowMismatchError(false);
			setIsFormValid(false);
			onProgress({
				password: newPassword,
				confirmPassword: newConfirmPassword
			});
		}
	};

	const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		if (/^[0-9]*$/.test(value) && value.length <= 6) {
			const newPassword = value;
			setPassword(newPassword);
			validatePasswords(newPassword, confirmPassword);
		}
	};

	const handleConfirmPasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.value;
		if (/^[0-9]*$/.test(value) && value.length <= 6) {
			const newConfirmPassword = value;
			setConfirmPassword(newConfirmPassword);
			validatePasswords(password, newConfirmPassword);
		}
	};

	const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			if (isFormValid) {
				onNext(password);
			}
			return;
		}

		if (!/^[0-9]$/.test(e.key) && e.key !== 'Backspace' && e.key !== 'Tab' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight') {
			e.preventDefault();
		}
	};

	const handleSubmit = (e: React.FormEvent) => {
		e.preventDefault();
		if (isFormValid) {
			onNext(password);
		}
	};

	return (
		<div
			className="password-form-section"
			ref={containerRef}>
			<div className="form-header">
				<h2>Şifre Belirle</h2>
			</div>

			<form
				onSubmit={handleSubmit}
				ref={formRef}>
				<FormInput
					label="Şifre"
					variant="password"
					name="password"
					value={password}
					onChange={handlePasswordChange}
					onKeyDown={handleKeyDown}
					placeholder="Şifrenizi yazın"
					inputMode="numeric"
					maxLength={6}
					showRequirements
					error={showMismatchError}
				/>

				<FormInput
					label="Şifre Tekrarı"
					variant="password"
					name="confirmPassword"
					value={confirmPassword}
					onChange={handleConfirmPasswordChange}
					onKeyDown={handleKeyDown}
					placeholder="Şifrenizi tekrar yazın"
					inputMode="numeric"
					maxLength={6}
					error={showMismatchError}
				/>

				{showMismatchError && (
					<div
						className="error-message"
						style={{ color: 'var(--danger-text)', marginTop: '8px' }}>
						<i className="icons-info-circle" />
						Şifreler birbiriyle uyuşmuyor!
					</div>
				)}

				<div
					style={{
						marginTop: 'auto',
						paddingTop: '16px'
					}}>
					<button
						type="submit"
						className="submit-button"
						disabled={!isFormValid || disabled}>
						Şifreni Belirle
					</button>
				</div>
			</form>
		</div>
	);
};
